import { motion } from 'framer-motion';
import { Building2, User, Mail, Phone, FileText, MapPin } from 'lucide-react';
import { OrderFormData } from '@/types/order';
import { Input } from '@/components/ui/input';

interface Step3DonneurProps {
  data: OrderFormData;
  onChange: (data: Partial<OrderFormData>) => void;
}

export function Step3Donneur({ data, onChange }: Step3DonneurProps) {
  return (
    <motion.div
      initial={{ opacity: 0, x: 20 }}
      animate={{ opacity: 1, x: 0 }}
      exit={{ opacity: 0, x: -20 }}
      className="space-y-8"
    >
      <div>
        <h3 className="text-3xl font-display font-bold text-navy-600 mb-2">
          Donneur d'ordre
        </h3>
        <p className="text-lg text-navy-400">Vos coordonnées professionnelles</p>
      </div>

      {/* Société / Étude */}
      <div>
        <label htmlFor="societe" className="flex items-center gap-2 text-sm font-medium text-navy-600 mb-2">
          <Building2 className="w-4 h-4 text-gold-600" />
          Société / Étude *
        </label>
        <Input
          id="societe"
          type="text"
          required
          value={data.societe}
          onChange={(e) => onChange({ societe: e.target.value })}
          placeholder="Nom de votre étude notariale"
        />
      </div>

      {/* Type professionnel (verrouillé) */}
      <div>
        <label className="block text-sm font-medium text-navy-600 mb-2">
          Type professionnel
        </label>
        <div className="w-full px-4 py-3 rounded-xl border border-navy-200 bg-cream text-navy-600 text-sm">
          Étude notariale
        </div>
      </div>

      {/* Nom & Prénom */}
      <div>
        <label htmlFor="nomPrenom" className="flex items-center gap-2 text-sm font-medium text-navy-600 mb-2">
          <User className="w-4 h-4 text-gold-600" />
          Nom & Prénom *
        </label>
        <Input
          id="nomPrenom"
          type="text"
          required
          value={data.nomPrenom}
          onChange={(e) => onChange({ nomPrenom: e.target.value })}
          placeholder="Jean Dupont"
        />
      </div>

      {/* Email & Téléphone */}
      <div className="grid md:grid-cols-2 gap-4">
        <div>
          <label htmlFor="email" className="flex items-center gap-2 text-sm font-medium text-navy-600 mb-2">
            <Mail className="w-4 h-4 text-gold-600" />
            Email *
          </label>
          <Input
            id="email"
            type="email"
            required
            value={data.email}
            onChange={(e) => onChange({ email: e.target.value })}
            placeholder="contact@etude.be"
          />
        </div>

        <div>
          <label htmlFor="telephone" className="flex items-center gap-2 text-sm font-medium text-navy-600 mb-2">
            <Phone className="w-4 h-4 text-gold-600" />
            Téléphone *
          </label>
          <Input
            id="telephone"
            type="tel"
            required
            value={data.telephone}
            onChange={(e) => onChange({ telephone: e.target.value })}
            placeholder="+32 XXX XX XX XX"
          />
        </div>
      </div>

      {/* Référence interne */}
      <div>
        <label htmlFor="referenceInterne" className="flex items-center gap-2 text-sm font-medium text-navy-600 mb-2">
          <FileText className="w-4 h-4 text-gold-600" />
          Référence dossier (optionnel)
        </label>
        <Input
          id="referenceInterne"
          type="text"
          value={data.referenceInterne}
          onChange={(e) => onChange({ referenceInterne: e.target.value })}
          placeholder="Réf. interne de votre dossier"
        />
      </div>

      {/* Adresse */}
      <div className="space-y-4 p-6 bg-cream rounded-2xl">
        <h4 className="flex items-center gap-2 font-semibold text-navy-600">
          <MapPin className="w-5 h-5 text-gold-600" />
          Adresse de l'étude
        </h4>

        <div>
          <label htmlFor="adresseDonneurRue" className="block text-sm font-medium text-navy-600 mb-2">
            Rue et numéro *
          </label>
          <Input
            id="adresseDonneurRue"
            type="text"
            required
            value={data.adresseDonneurRue}
            onChange={(e) => onChange({ adresseDonneurRue: e.target.value })}
            placeholder="Rue de la Loi, 123"
          />
        </div>

        <div className="grid md:grid-cols-2 gap-4">
          <div>
            <label htmlFor="adresseDonneurCP" className="block text-sm font-medium text-navy-600 mb-2">
              Code postal *
            </label>
            <Input
              id="adresseDonneurCP"
              type="text"
              required
              value={data.adresseDonneurCP}
              onChange={(e) => onChange({ adresseDonneurCP: e.target.value })}
              placeholder="1000"
            />
          </div>

          <div>
            <label htmlFor="adresseDonneurVille" className="block text-sm font-medium text-navy-600 mb-2">
              Ville *
            </label>
            <Input
              id="adresseDonneurVille"
              type="text"
              required
              value={data.adresseDonneurVille}
              onChange={(e) => onChange({ adresseDonneurVille: e.target.value })}
              placeholder="Bruxelles"
            />
          </div>
        </div>
      </div>

      {/* Numéro TVA */}
      <div>
        <label htmlFor="tvaNumber" className="block text-sm font-medium text-navy-600 mb-2">
          Numéro TVA (optionnel)
        </label>
        <Input
          id="tvaNumber"
          type="text"
          value={data.tvaNumber}
          onChange={(e) => onChange({ tvaNumber: e.target.value })}
          placeholder="BE 0XXX.XXX.XXX"
        />
      </div>

      {/* Adresse de facturation */}
      <div>
        <label htmlFor="adresseFacturation" className="block text-sm font-medium text-navy-600 mb-2">
          Adresse de facturation (optionnel)
        </label>
        <p className="text-xs text-navy-400 mb-2">
          Si différente de l'adresse de l'étude
        </p>
        <textarea
          id="adresseFacturation"
          rows={2}
          value={data.adresseFacturation}
          onChange={(e) => onChange({ adresseFacturation: e.target.value })}
          placeholder="Adresse complète de facturation..."
          className="w-full px-4 py-3 rounded-xl border border-navy-200 focus:border-gold-500 focus:outline-none focus:ring-4 focus:ring-gold-500/20 transition-all resize-none text-navy-600 placeholder:text-navy-300"
        />
      </div>
    </motion.div>
  );
}
